import {
    AuthenticatedProvider,
    Container,
    Layout,
    NavBar as NewSideBar,
    useScreen,
    useSizes,
    withAnalytics,
    withSagas,
} from 'metrix-common3';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import { executeAssignmentAction } from '../../actions/policies/executeAssignmentAction';
import { getBoardPolicies } from '../../actions/policies/getBoardPolicies';
import { getBordereauDownloadUrl } from '../../actions/policies/getBordereauDownloadUrl';
import { getBordereauSummaries } from '../../actions/policies/getBordereauSummaries';

import { getBoundPolicies } from '../../actions/policies/getBoundPolicies';
import { getListPolicies } from '../../actions/policies/getListPolicies';
import { getTriagePolicies } from '../../actions/policies/getTriagePolicies';
import { queryBoundPolicies } from '../../actions/policies/queryBoundPolicies';
import { queryPolicies } from '../../actions/policies/queryPolicies';
import { queryTriagePolicies } from '../../actions/policies/queryTriagePolicies';
import { reduceQueryElement } from '../../actions/policies/reduceQueryElement';
import { reduceView } from '../../actions/policies/reduceView';
import { getProduct } from '../../actions/product/getProduct';
import AppBar from '../../components/AppBar';
import PoliciesBoard from '../../components/Policies/Board';
import PoliciesBordereaux from '../../components/Policies/Bordereaux';
import PoliciesBound from '../../components/Policies/Bound';
import PoliciesHeader from '../../components/Policies/Header';
import PoliciesIncomplete from '../../components/Policies/Incomplete';
import PoliciesList from '../../components/Policies/List';
import IconList from '../../icons';
import policiesSaga from '../../sagas/policies/saga';
import productSaga from '../../sagas/product/saga';
import { selectPolicies } from '../../selectors/policies/selectPolicies';
import { selectQuery } from '../../selectors/policies/selectQuery';
import { selectView } from '../../selectors/policies/selectView';
import { selectProducts } from '../../selectors/product/selectProducts';
import configs from './config.json';

const PoliciesContainer = styled(Container)`
  ${({ $desktop }) => $desktop && 'width: 83vw;'}
`;

const Policies = (props) => {
    const location = useLocation();
    const navigate = useNavigate();
    const intl = useIntl();
    const sizes = useSizes();
    const { isDesktop: desktop } = useScreen();
    const menu = location.pathname.split('/')[1] || 'policies';
    const isBordereaux = location.pathname.startsWith('/bordereaux');

    // The List filter starts collapsed; the header owns the toggle button, the list owns the form,
    // so the shared state lives here. hasActiveFilters (from the list's query) drives the dot on the
    // collapsed toggle.
    const [showFilters, setShowFilters] = useState(false);
    const [hasActiveFilters, setHasActiveFilters] = useState(false);

    // Remember the last-active Policies sub-tab so returning to Policies (e.g. from a policy profile
    // via the sidebar) lands back on it, not on All. Read once at mount, before the save effect below
    // overwrites it, then redirect if we arrived on the All index but a sub-tab was last active.
    const returnTab = useRef(sessionStorage.getItem('policies.activeTab')).current;

    // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only redirect to the remembered tab
    useEffect(() => {
        if (location.pathname === '/policies' && returnTab?.startsWith('/policies/')) {
            navigate(returnTab, { replace: true });
        }
    }, []);

    useEffect(() => {
        if (location.pathname.startsWith('/policies')) {
            sessionStorage.setItem('policies.activeTab', location.pathname);
        }
    }, [location.pathname]);

    if (props.context.session.userType !== 'administrator' && props.context.session.userType !== 'broker') {
        window.location = '/403';
    }

    // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only init
    useEffect(() => {
        props.doGetProduct();
        props.reference.doGetReferences('ActiveBrokers');
        props.reference.doGetReferences('ActiveBrokerCompanies');

        if (!props.context.storefrontUrl) {
            props.context.doGetStorefrontUrl();
        }
    }, []);

    // Process the config for internationalization
    const processConfigForI18n = (config) => {
        if (!config) return null;

        // Deep clone the config to avoid modifying the original
        const localizedConfig = JSON.parse(JSON.stringify(config));

        // Process links
        if (localizedConfig.links) {
            localizedConfig.links = localizedConfig.links.map((link) => {
                // Translate the label using intl.formatMessage
                link.label = intl.formatMessage({
                    id: `Policies.${link.code}`,
                    defaultMessage: link.label,
                });
                return link;
            });
        }

        return localizedConfig;
    };

    const config = configs.filter(
        (config) =>
            config.auth === props.context.authenticated &&
            ((props.context.session && config.userType === props.context.session.userType) ||
                config.userType === 'any'),
    )[0];

    // Apply internationalization to the config
    const localizedConfig = processConfigForI18n(config);

    let tabs = localizedConfig?.links || [];
    if (!props.context.storefrontUrl) {
        tabs = tabs.filter((tab) => tab.value !== 'newquote');
    }

    const handleClick = (target) => {
        const clientWeb = props?.context?.configuration?.clientWeb;

        switch (target) {
            case 'policies':
                // Already in Policies? Keep the current sub-tab (Incomplete/Bound/Board) instead of
                // resetting to All.
                if (!location.pathname.startsWith('/policies')) {
                    navigate('/policies');
                }
                break;
            case 'bordereaux':
                navigate(`/${target}`);
                break;
            case 'search':
                window.location = `${clientWeb}/search`;
                break;
            case 'leads':
                window.location = `${clientWeb}/leads`;
                break;
            case 'newquote':
                window.open(props.context.storefrontUrl, '_blank');
                setTimeout(() => {
                    navigate('/');
                }, 1000);
                break;
            default:
                navigate('/404');
                break;
        }
    };

    // Translate assignees and broker companies labels
    let assignees = props.reference.references?.ActiveBrokers || [];
    assignees = [
        {
            code: null,
            value: intl.formatMessage({ id: 'Policies.allBrokers', defaultMessage: 'All Brokers' }),
        },
        ...assignees,
    ];

    let brokerCompanies = props.reference.references?.ActiveBrokerCompanies || [];
    brokerCompanies = [
        {
            code: null,
            value: intl.formatMessage({ id: 'Policies.allBrokerage', defaultMessage: 'All Brokerage' }),
        },
        ...brokerCompanies,
    ];

    return (
        <AuthenticatedProvider {...props}>
            <Layout
                appbar={<AppBar {...props} history={{ push: navigate }} />}
                sideContent={
                    <NewSideBar
                        tabs={tabs}
                        defaultTab={menu}
                        clickEvent={(value) => handleClick(value)}
                        IconList={IconList}
                        {...props}
                    />
                }
                loading={props.context.loading}
                noWidth
            >
                {!isBordereaux && (
                    <PoliciesHeader
                        {...props}
                        assignees={assignees}
                        brokerCompanies={brokerCompanies}
                        showFilters={showFilters}
                        setShowFilters={setShowFilters}
                        hasActiveFilters={hasActiveFilters}
                    />
                )}
                <PoliciesContainer $desktop={desktop} bottom={sizes.xs}>
                    <Routes>
                        {isBordereaux ? (
                            <Route
                                index
                                element={
                                    <PoliciesBordereaux
                                        context={props.context}
                                        navigate={navigate}
                                        history={{ push: navigate }}
                                        reference={props.reference}
                                        doReduceView={props.doReduceView}
                                        bordereauSummaries={props.bordereauSummaries}
                                        doGetBordereauSummaries={props.doGetBordereauSummaries}
                                        doGetBordereauDownloadUrl={props.doGetBordereauDownloadUrl}
                                    />
                                }
                            />
                        ) : (
                            <>
                                <Route
                                    index
                                    element={
                                        <PoliciesList
                                            context={props.context}
                                            navigate={navigate}
                                            history={{ push: navigate }}
                                            query={props.query}
                                            products={props.products}
                                            reference={props.reference}
                                            doReduceView={props.doReduceView}
                                            doReduceQueryElement={props.doReduceQueryElement}
                                            listPolicies={props.listPolicies}
                                            doGetListPolicies={props.doGetListPolicies}
                                            doQueryPolicies={props.doQueryPolicies}
                                            showFilters={showFilters}
                                            onActiveFiltersChange={setHasActiveFilters}
                                        />
                                    }
                                />
                                <Route
                                    path='incomplete'
                                    element={
                                        <PoliciesIncomplete
                                            context={props.context}
                                            navigate={navigate}
                                            history={{ push: navigate }}
                                            query={props.query}
                                            products={props.products}
                                            reference={props.reference}
                                            doReduceView={props.doReduceView}
                                            doReduceQueryElement={props.doReduceQueryElement}
                                            incompletePolicies={props.incompletePolicies}
                                            doGetTriagePolicies={props.doGetTriagePolicies}
                                            doQueryTriagePolicies={props.doQueryTriagePolicies}
                                            showFilters={showFilters}
                                            onActiveFiltersChange={setHasActiveFilters}
                                        />
                                    }
                                />
                                <Route
                                    path='bound'
                                    element={
                                        <PoliciesBound
                                            context={props.context}
                                            navigate={navigate}
                                            history={{ push: navigate }}
                                            query={props.query}
                                            products={props.products}
                                            reference={props.reference}
                                            doReduceView={props.doReduceView}
                                            doReduceQueryElement={props.doReduceQueryElement}
                                            boundPolicies={props.boundPolicies}
                                            doGetBoundPolicies={props.doGetBoundPolicies}
                                            doQueryBoundPolicies={props.doQueryBoundPolicies}
                                            showFilters={showFilters}
                                            onActiveFiltersChange={setHasActiveFilters}
                                        />
                                    }
                                />
                                <Route
                                    path='board'
                                    element={
                                        <PoliciesBoard
                                            context={props.context}
                                            navigate={navigate}
                                            history={{ push: navigate }}
                                            query={props.query}
                                            products={props.products}
                                            reference={props.reference}
                                            doReduceView={props.doReduceView}
                                            boardPolicies={props.boardPolicies}
                                            doGetBoardPolicies={props.doGetBoardPolicies}
                                        />
                                    }
                                />
                            </>
                        )}
                    </Routes>
                </PoliciesContainer>
            </Layout>
        </AuthenticatedProvider>
    );
};

const mapStateToProps = createStructuredSelector({
    boundPolicies: selectPolicies('boundPolicies'),
    boardPolicies: selectPolicies('boardPolicies'),
    listPolicies: selectPolicies('listPolicies'),
    incompletePolicies: selectPolicies('triagePolicies'),
    bordereauSummaries: selectPolicies('bordereauSummaries'),
    query: selectQuery(),
    products: selectProducts(),
    view: selectView(),
});

function mapDispatchToProps(dispatch) {
    return {
        doGetProduct: () => dispatch(getProduct()),
        doGetBoardPolicies: (payload) => dispatch(getBoardPolicies(payload)),
        doGetBoundPolicies: (payload) => dispatch(getBoundPolicies(payload)),
        doGetListPolicies: () => dispatch(getListPolicies()),
        doGetTriagePolicies: () => dispatch(getTriagePolicies()),
        doQueryPolicies: (payload) => dispatch(queryPolicies(payload)),
        doQueryTriagePolicies: (payload) => dispatch(queryTriagePolicies(payload)),
        doQueryBoundPolicies: (payload) => dispatch(queryBoundPolicies(payload)),
        doReduceQueryElement: (payload) => dispatch(reduceQueryElement(payload)),
        doExecuteAssignmentAction: (payload) => dispatch(executeAssignmentAction(payload)),
        doReduceView: (payload) => dispatch(reduceView(payload)),
        doGetBordereauSummaries: (payload) => dispatch(getBordereauSummaries(payload)),
        doGetBordereauDownloadUrl: (payload) => dispatch(getBordereauDownloadUrl(payload)),
    };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

const withPoliciesSaga = withSagas({ key: 'policies', saga: policiesSaga });
const withProductSaga = withSagas({ key: 'product', saga: productSaga });

const WrappedPolicies = withAnalytics(Policies, true);

export default compose(withConnect, withPoliciesSaga, withProductSaga)(WrappedPolicies);
